import React from 'react';
import {
    Box,
    Button,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import {
    ViewModule,
    CalendarViewMonth,
    ChevronLeft,
    ChevronRight,
    Visibility,
    VisibilityOff
} from '@mui/icons-material';
import moment from 'moment';

interface ScheduleViewControlsProps {
    viewMode: string;
    setViewMode: (newMode: string) => void;
    currentWeek: moment.Moment;
    setCurrentWeek: (newWeek: moment.Moment) => void;
    isDarkMode: boolean;
    availabilityMode: boolean;
    setAvailabilityMode: (mode: boolean) => void;
}

const ScheduleViewControls: React.FC<ScheduleViewControlsProps> = ({
    viewMode,
    setViewMode,
    currentWeek,
    setCurrentWeek,
    isDarkMode,
    availabilityMode,
    setAvailabilityMode
}) => {
    const handleViewModeChange = (event: any, newMode: string | null) => {
        if (newMode !== null) {
            setViewMode(newMode);
            setCurrentWeek(moment());
        }
    };

    return (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 1 },
                    flexWrap: 'wrap'
                }}
            >
                {/* View Mode Toggle */}
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    size="small"
                    sx={{
                        order: { xs: 2, sm: 1 },
                        '& .MuiToggleButton-root': {
                            minWidth: { xs: 40, sm: 44, md: 48 },
                            height: { xs: 32, sm: 36, md: 40 },
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            px: { xs: 1, sm: 1.5, md: 2 }
                        }
                    }}
                >
                    <ToggleButton value="week" aria-label="Week view">
                        <ViewModule fontSize="small" />
                        <Typography
                            variant="caption"
                            sx={{
                                ml: { xs: 0, sm: 0.5 },
                                display: { xs: 'inline', sm: 'inline' },
                                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' }
                            }}
                        >
                            Week
                        </Typography>
                    </ToggleButton>
                    <ToggleButton value="month" aria-label="Month view">
                        <CalendarViewMonth fontSize="small" />
                        <Typography
                            variant="caption"
                            sx={{
                                ml: { xs: 0, sm: 0.5 },
                                display: { xs: 'inline', sm: 'inline' },
                                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' }
                            }}
                        >
                            Month
                        </Typography>
                    </ToggleButton>
                </ToggleButtonGroup>

                {/* Navigation Controls */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 0.5, sm: 1 },
                        order: { xs: 1, sm: 2 },
                        width: { xs: '100%', sm: 'auto' },
                        justifyContent: { xs: 'center', sm: 'flex-end' }
                    }}
                >
                    {/* Availability Toggle */}
                    <Button
                        variant="contained"
                        color="info"
                        size="small"
                        sx={{
                            minWidth: { xs: 60, sm: 70, md: 80 },
                            height: { xs: 32, sm: 36, md: 40 },
                            fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                            fontWeight: 800,
                            color: 'white',
                            bgcolor: 'primary.main',
                            '&:hover': {
                                bgcolor: 'primary.dark'
                            },
                            px: { xs: 1, sm: 1.5, md: 2 }
                        }}
                        onClick={() => setAvailabilityMode(!availabilityMode)}
                    >
                        AVAILABILITY {availabilityMode ? <Visibility sx={{ ml: 1, display: { xs: 'none', sm: 'inline' } }} /> : <VisibilityOff sx={{ ml: 1, display: { xs: 'none', sm: 'inline' } }} />}
                    </Button>
                    {/* Today Button */}
                    <Button
                        variant="contained"
                        color="info"
                        size="small"
                        onClick={() => setCurrentWeek(moment())}
                        sx={{
                            minWidth: { xs: 60, sm: 70, md: 80 },
                            height: { xs: 32, sm: 36, md: 40 },
                            fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                            fontWeight: 800,
                            color: 'white',
                            bgcolor: 'info.main',
                            '&:hover': {
                                bgcolor: 'info.dark'
                            },
                            px: { xs: 1, sm: 1.5, md: 2 }
                        }}
                    >
                        TODAY
                    </Button>

                    {/* Previous Button */}
                    <IconButton
                        size="small"
                        onClick={() => {
                            if (viewMode === 'month') {
                                setCurrentWeek(moment(currentWeek).subtract(1, 'month'));
                            } else {
                                setCurrentWeek(moment(currentWeek).subtract(1, 'week'));
                            }
                        }}
                        sx={{
                            width: { xs: 32, sm: 36, md: 40 },
                            height: { xs: 32, sm: 36, md: 40 },
                            borderRadius: 1,
                            '&:hover': {
                                bgcolor: 'action.hover'
                            }
                        }}
                        aria-label="Previous period"
                    >
                        <ChevronLeft
                            sx={{
                                color: 'text.secondary',
                                fontSize: { xs: 18, sm: 20, md: 24 }
                            }}
                        />
                    </IconButton>

                    {/* Date Display */}
                    <Box
                        sx={{
                            minWidth: { xs: 140, sm: 180, md: 220 },
                            textAlign: 'center',
                            px: { xs: 0.5, sm: 1, md: 2 }
                        }}
                    >
                        <Box
                            sx={{
                                fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                                fontWeight: 700,
                                lineHeight: 1.2,
                                whiteSpace: { xs: 'nowrap', sm: 'normal' },
                                overflow: { xs: 'hidden', sm: 'visible' },
                                textOverflow: { xs: 'ellipsis', sm: 'clip' }
                            }}
                        >
                            {viewMode === 'month' ? (
                                <Box>
                                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                        {currentWeek.format('MMMM YYYY')}
                                    </Box>
                                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                                        {currentWeek.format('MMM YYYY')}
                                    </Box>
                                </Box>
                            ) : (
                                <Box>
                                    <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                                        Week of {currentWeek.startOf('week').format('MMM D')} -{' '}
                                        {currentWeek.endOf('week').format('MMM D, YYYY')}
                                    </Box>
                                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline', md: 'none' } }}>
                                        {currentWeek.startOf('week').format('MMM D')} -{' '}
                                        {currentWeek.endOf('week').format('MMM D')}
                                    </Box>
                                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                                        {currentWeek.startOf('week').format('M/D')} -{' '}
                                        {currentWeek.endOf('week').format('M/D')}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Next Button */}
                    <IconButton
                        size="small"
                        onClick={() => {
                            if (viewMode === 'month') {
                                setCurrentWeek(moment(currentWeek).add(1, 'month'));
                            } else {
                                setCurrentWeek(moment(currentWeek).add(1, 'week'));
                            }
                        }}
                        sx={{
                            width: { xs: 32, sm: 36, md: 40 },
                            height: { xs: 32, sm: 36, md: 40 },
                            borderRadius: 1,
                            '&:hover': {
                                bgcolor: 'action.hover'
                            }
                        }}
                        aria-label="Next period"
                    >
                        <ChevronRight
                            sx={{
                                color: 'text.secondary',
                                fontSize: { xs: 18, sm: 20, md: 24 }
                            }}
                        />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
};

export default ScheduleViewControls;